import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft, Shield, KeyRound, Send, RefreshCw, FileText, MessageCircle, Download, FileJson,
  TrendingUp, ReceiptText, Loader2, CheckCircle2, XCircle, Calendar, Wallet,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api, API, getErr } from "../../lib/api";

const FY_OPTIONS = ["2026-27", "2025-26", "2024-25", "2023-24"];
const MONTHS = [
  ["01-2026", "January 2026"], ["02-2026", "February 2026"], ["03-2026", "March 2026"],
  ["04-2026", "April 2026"], ["05-2026", "May 2026"], ["06-2026", "June 2026"],
  ["07-2026", "July 2026"], ["08-2026", "August 2026"], ["09-2026", "September 2026"],
  ["10-2026", "October 2026"], ["11-2026", "November 2026"], ["12-2026", "December 2026"],
];

export default function AdminCustomerGst() {
  const { id: customerId } = useParams();
  const [customer, setCustomer] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [returns, setReturns] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [bills, setBills] = useState(null);
  const [adminWa, setAdminWa] = useState("7008144614");

  const loadAll = async () => {
    try {
      const [c, st, rs, an, lb, cfg] = await Promise.all([
        api.get(`/admin/customers/${customerId}`),
        api.get(`/admin/customers/${customerId}/gst/status`),
        api.get(`/admin/customers/${customerId}/gst/returns`),
        api.get(`/admin/customers/${customerId}/gst/analytics`),
        api.get(`/admin/customers/${customerId}/gst/latest-bills`),
        api.get(`/config/public`).catch(() => ({ data: {} })),
      ]);
      setCustomer(c.data);
      setAccounts(st.data.accounts || []);
      setReturns(rs.data.returns || []);
      setAnalytics(an.data);
      setBills(lb.data);
      if (cfg.data?.admin_whatsapp) setAdminWa(cfg.data.admin_whatsapp);
    } catch (e) {
      toast.error(getErr(e, "Could not load GST data"));
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [customerId]);

  const connectedAccount = useMemo(() => accounts.find((a) => a.status === "connected"), [accounts]);

  if (!customer) return <div className="p-10"><Loader2 className="animate-spin text-[#1E4D3B]" /></div>;

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-6xl mx-auto" data-testid="admin-customer-gst-page">
      <Toaster richColors position="top-right" />
      <Link to={`/admin/customers/${customerId}`} className="inline-flex items-center gap-1.5 text-sm text-[#5C6661] hover:text-[#1E4D3B] mb-3">
        <ArrowLeft size={14} /> Back to customer
      </Link>

      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <span className="label-eyebrow">GST Module</span>
          <h1 className="mt-2 text-3xl font-display font-medium text-[#121C17]">{customer.full_name}</h1>
          <p className="text-sm text-[#5C6661]">Ref <span className="font-mono">{customer.ref}</span> · Mobile +91 {customer.mobile}</p>
        </div>
        {connectedAccount ? (
          <span className="self-start inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1.5 text-xs font-medium border border-emerald-200" data-testid="gst-status-pill">
            <Shield size={12} /> Connected · {connectedAccount.gstin}
          </span>
        ) : (
          <span className="self-start inline-flex items-center gap-1.5 rounded-full bg-amber-50 text-amber-700 px-3 py-1.5 text-xs font-medium border border-amber-200">
            <Shield size={12} /> Not connected
          </span>
        )}
      </div>

      <ConnectPanel customerId={customerId} accounts={accounts} onChange={loadAll} />

      {connectedAccount && (
        <>
          <FetchPanel customerId={customerId} gstin={connectedAccount.gstin} onFetched={loadAll} />
          <ReturnsList returns={returns} customerId={customerId} customer={customer} adminWa={adminWa} onLog={() => loadAll()} />
          <AnalyticsPanel analytics={analytics} bills={bills} />
        </>
      )}
    </div>
  );
}

function ConnectPanel({ customerId, accounts, onChange }) {
  const [gstin, setGstin] = useState("");
  const [legalName, setLegalName] = useState("");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [demoOtp, setDemoOtp] = useState(null);
  const [busy, setBusy] = useState(false);

  const onConnect = async (e) => {
    e?.preventDefault();
    if (!gstin || gstin.length !== 15) return toast.error("Enter a 15-character GSTIN");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/customers/${customerId}/gst/connect`, {
        gstin: gstin.toUpperCase(), legal_name: legalName, mobile,
      });
      setOtpSent(true);
      setDemoOtp(data.demo_otp);
      toast.success(`OTP sent to ${data.otp_sent_to}`);
    } catch (e) { toast.error(getErr(e, "Connect failed")); }
    finally { setBusy(false); }
  };

  const onVerify = async (e) => {
    e?.preventDefault();
    if (!otp || otp.length !== 6) return toast.error("Enter the 6-digit OTP");
    setBusy(true);
    try {
      await api.post(`/admin/customers/${customerId}/gst/verify-otp`, {
        gstin: gstin.toUpperCase(), otp,
      });
      toast.success("GST account connected");
      setOtp(""); setOtpSent(false); setDemoOtp(null); setGstin(""); setLegalName(""); setMobile("");
      onChange();
    } catch (e) { toast.error(getErr(e, "OTP verification failed")); }
    finally { setBusy(false); }
  };

  const onDisconnect = async (g) => {
    if (!window.confirm(`Disconnect ${g}?`)) return;
    try {
      await api.post(`/admin/customers/${customerId}/gst/disconnect`, null, { params: { gstin: g } });
      toast.success("Disconnected");
      onChange();
    } catch (e) { toast.error(getErr(e, "Disconnect failed")); }
  };

  return (
    <div className="bento-card p-5 sm:p-6 mb-5" data-testid="gst-connect-panel">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound size={16} className="text-[#1E4D3B]" />
        <h2 className="font-display font-medium text-lg text-[#121C17]">Connect GST account</h2>
      </div>

      {accounts.length > 0 && (
        <div className="space-y-2 mb-5">
          {accounts.map((a) => (
            <div key={a.gstin} className={`flex flex-wrap items-center gap-2 p-3 rounded-xl border ${a.status === "connected" ? "border-emerald-200 bg-emerald-50/30" : "border-[#E5E2DC]"}`} data-testid={`gst-account-${a.gstin}`}>
              <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === "connected" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{a.status}</span>
              <span className="font-mono text-sm">{a.gstin}</span>
              <span className="text-xs text-[#5C6661]">{a.legal_name}</span>
              {a.status === "connected" && (
                <button onClick={() => onDisconnect(a.gstin)} className="ml-auto text-xs text-red-600 hover:underline" data-testid={`gst-disconnect-${a.gstin}`}>Disconnect</button>
              )}
            </div>
          ))}
        </div>
      )}

      {!otpSent ? (
        <form onSubmit={onConnect} className="grid sm:grid-cols-4 gap-3" data-testid="gst-connect-form">
          <input value={gstin} onChange={(e) => setGstin(e.target.value.toUpperCase())}
            placeholder="GSTIN (15 chars)" maxLength={15}
            data-testid="gst-input-gstin"
            className="font-mono bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B]" />
          <input value={legalName} onChange={(e) => setLegalName(e.target.value)}
            placeholder="Legal name (optional)"
            data-testid="gst-input-legal-name"
            className="bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B] sm:col-span-2" />
          <input value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder="OTP target mobile"
            data-testid="gst-input-mobile"
            className="bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B]" />
          <button disabled={busy} type="submit" data-testid="gst-send-otp"
            className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2 sm:col-span-4 sm:w-fit disabled:opacity-60">
            <Send size={14} /> {busy ? "Sending OTP…" : "Send OTP"}
          </button>
        </form>
      ) : (
        <form onSubmit={onVerify} className="grid sm:grid-cols-3 gap-3" data-testid="gst-otp-form">
          <div className="sm:col-span-2 text-sm text-[#5C6661]">
            OTP sent for <span className="font-mono text-[#121C17]">{gstin}</span>.
            {demoOtp && (
              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                Demo OTP: <code>{demoOtp}</code>
              </span>
            )}
          </div>
          <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="6-digit OTP" autoFocus
            data-testid="gst-input-otp"
            className="font-mono tracking-widest text-center bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B]" />
          <button disabled={busy} type="submit" data-testid="gst-verify-otp"
            className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
            <CheckCircle2 size={14} /> Verify & connect
          </button>
          <button type="button" onClick={() => { setOtpSent(false); setOtp(""); setDemoOtp(null); }}
            data-testid="gst-cancel-otp"
            className="text-xs text-[#5C6661] hover:underline self-start sm:col-span-3 justify-self-start">← Cancel & re-enter</button>
        </form>
      )}
    </div>
  );
}

function FetchPanel({ customerId, gstin, onFetched }) {
  const [fy, setFy] = useState("2025-26");
  const [returnType, setReturnType] = useState("BOTH");
  const [period, setPeriod] = useState("04-2026");
  const [busy, setBusy] = useState(false);

  const onFetch = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/customers/${customerId}/gst/fetch-returns`, {
        gstin, financial_year: fy, return_type: returnType, period,
      });
      toast.success(`Fetched ${returnType} for ${period}`);
      onFetched && onFetched();
    } catch (e) { toast.error(getErr(e, "Fetch failed")); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={onFetch} className="bento-card p-5 sm:p-6 mb-5" data-testid="gst-fetch-panel">
      <div className="flex items-center gap-2 mb-4">
        <RefreshCw size={16} className="text-[#1E4D3B]" />
        <h2 className="font-display font-medium text-lg text-[#121C17]">Fetch returns</h2>
      </div>
      <div className="grid sm:grid-cols-4 gap-3">
        <label className="block">
          <span className="text-xs text-[#5C6661]">Financial year</span>
          <select value={fy} onChange={(e) => setFy(e.target.value)} data-testid="gst-fy"
            className="mt-1 w-full bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B]">
            {FY_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-[#5C6661]">Return type</span>
          <select value={returnType} onChange={(e) => setReturnType(e.target.value)} data-testid="gst-return-type"
            className="mt-1 w-full bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B]">
            <option>BOTH</option><option>GSTR1</option><option>GSTR3B</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs text-[#5C6661]">Filing month</span>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="gst-period"
            className="mt-1 w-full bg-white border border-[#E5E2DC] rounded-xl px-3 py-2.5 text-sm focus:border-[#1E4D3B]">
            {MONTHS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
        <button disabled={busy} type="submit" data-testid="gst-fetch-submit"
          className="btn-primary rounded-full px-5 py-2.5 text-sm font-medium inline-flex items-center justify-center gap-2 self-end disabled:opacity-60">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} {busy ? "Fetching…" : "Fetch & download"}
        </button>
      </div>
      <p className="mt-3 text-[11px] text-[#5C6661]">
        Returns are fetched via your GSP (currently mocked with realistic sample data), PDFs + JSON auto-stored, and tax summary is computed instantly.
      </p>
    </form>
  );
}

function ReturnsList({ returns, customerId, customer, adminWa, onLog }) {
  if (!returns.length) return null;

  const buildWaMessage = (r) => {
    const lines = [
      `Hello ${customer.full_name},`,
      "",
      `Your GST Returns for *${r.period_label}* have been successfully fetched.`,
      "",
      `✅ ${r.return_type === "GSTR1" ? "GSTR1" : r.return_type === "GSTR3B" ? "GSTR3B" : "GSTR1 & GSTR3B"} Status: ${r.filed ? "Filed" : "Pending"}`,
      `✅ ARN Number: ${r.arn}`,
      "",
      "*Tax Summary:*",
      `• CGST: ₹${(r.summary?.cgst || 0).toLocaleString("en-IN")}`,
      `• SGST: ₹${(r.summary?.sgst || 0).toLocaleString("en-IN")}`,
      `• IGST: ₹${(r.summary?.igst || 0).toLocaleString("en-IN")}`,
      `• Total tax: ₹${(r.summary?.total_tax || 0).toLocaleString("en-IN")}`,
      `• ITC: ₹${(r.summary?.input_tax_credit || 0).toLocaleString("en-IN")}`,
      "",
      "*Latest Bill Numbers:*",
      `• Last invoice no: ${r.summary?.last_invoice_no || "—"}`,
      `• Last bill date: ${(r.summary?.last_invoice_date || "").slice(0, 10)}`,
      `• Invoice count: ${r.summary?.invoice_count || 0}`,
      "",
      "Your return files:",
      r.gstr1_path ? `📄 GSTR1: ${API}/admin/customers/${customerId}/gst/returns/${r.id}/file/gstr1_pdf` : null,
      r.gstr3b_path ? `📄 GSTR3B: ${API}/admin/customers/${customerId}/gst/returns/${r.id}/file/gstr3b_pdf` : null,
      "",
      "— Team Finsahayak.com",
    ].filter(Boolean);
    return lines.join("\n");
  };

  const onShareWa = async (r) => {
    const wa = (customer.whatsapp || customer.mobile || "").replace(/\D/g, "");
    const wn = wa.length === 10 ? `91${wa}` : wa;
    const msg = buildWaMessage(r);
    window.open(`https://wa.me/${wn}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
    try {
      await api.post(`/admin/customers/${customerId}/gst/whatsapp-log`, {
        return_id: r.id, channel: "wa.me", template: "gst_return_share", message: msg.slice(0, 280),
      });
      onLog && onLog();
    } catch { /* logging is best-effort */ }
  };

  return (
    <div className="bento-card p-5 sm:p-6 mb-5" data-testid="gst-returns-list">
      <div className="flex items-center gap-2 mb-4">
        <FileText size={16} className="text-[#1E4D3B]" />
        <h2 className="font-display font-medium text-lg text-[#121C17]">Fetched returns <span className="text-sm text-[#5C6661]">({returns.length})</span></h2>
      </div>
      <div className="space-y-3">
        {returns.map((r) => (
          <div key={r.id} className="p-4 rounded-xl border border-[#E5E2DC] hover:border-[#1E4D3B]/30 transition-colors" data-testid={`gst-return-${r.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[180px]">
                <div className="font-medium text-[#121C17] flex items-center gap-2">
                  {r.return_type} · {r.period_label}
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${r.filed ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {r.filed ? <span className="inline-flex items-center gap-1"><CheckCircle2 size={10} /> Filed</span> : "Pending"}
                  </span>
                </div>
                <div className="text-xs text-[#5C6661] mt-0.5">ARN <span className="font-mono">{r.arn}</span> · Fetched {(r.fetched_at || "").slice(0, 10)}</div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {r.gstr1_path && (
                  <a href={`${API}/admin/customers/${customerId}/gst/returns/${r.id}/file/gstr1_pdf`} target="_blank" rel="noopener noreferrer"
                    data-testid={`gst-download-r1-${r.id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#1E4D3B] hover:underline border border-[#E5E2DC] rounded-full px-3 py-1.5 bg-white">
                    <Download size={11} /> GSTR1 PDF
                  </a>
                )}
                {r.gstr3b_path && (
                  <a href={`${API}/admin/customers/${customerId}/gst/returns/${r.id}/file/gstr3b_pdf`} target="_blank" rel="noopener noreferrer"
                    data-testid={`gst-download-r3b-${r.id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#1E4D3B] hover:underline border border-[#E5E2DC] rounded-full px-3 py-1.5 bg-white">
                    <Download size={11} /> GSTR3B PDF
                  </a>
                )}
                {(r.gstr1_json_path || r.both_json_path) && (
                  <a href={`${API}/admin/customers/${customerId}/gst/returns/${r.id}/file/gstr1_json`} target="_blank" rel="noopener noreferrer"
                    data-testid={`gst-download-json-${r.id}`}
                    className="inline-flex items-center gap-1 text-xs font-medium text-[#5C6661] hover:underline border border-[#E5E2DC] rounded-full px-3 py-1.5 bg-white">
                    <FileJson size={11} /> JSON
                  </a>
                )}
                <button onClick={() => onShareWa(r)} data-testid={`gst-share-wa-${r.id}`}
                  className="inline-flex items-center gap-1 text-xs font-medium text-white rounded-full px-3 py-1.5"
                  style={{ background: "#25D366" }}>
                  <MessageCircle size={11} /> WhatsApp
                </button>
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
              <Pill label="Total tax" value={`₹${(r.summary?.total_tax || 0).toLocaleString("en-IN")}`} />
              <Pill label="ITC" value={`₹${(r.summary?.input_tax_credit || 0).toLocaleString("en-IN")}`} />
              <Pill label="Last inv" value={r.summary?.last_invoice_no || "—"} />
              <Pill label="Invoices" value={r.summary?.invoice_count || 0} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Pill({ label, value }) {
  return (
    <div className="bg-[#F3F1EC] rounded-lg px-2.5 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-[#5C6661]">{label}</div>
      <div className="text-[#121C17] font-medium truncate">{value}</div>
    </div>
  );
}

function AnalyticsPanel({ analytics, bills }) {
  if (!analytics) return null;
  const { totals, monthly } = analytics;

  return (
    <div className="bento-card p-5 sm:p-6" data-testid="gst-analytics">
      <div className="flex items-center gap-2 mb-5">
        <TrendingUp size={16} className="text-[#1E4D3B]" />
        <h2 className="font-display font-medium text-lg text-[#121C17]">Tax analytics</h2>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card label="CGST" value={totals.cgst} color="#1E4D3B" />
        <Card label="SGST" value={totals.sgst} color="#1E4D3B" />
        <Card label="IGST" value={totals.igst} color="#D17B5D" />
        <Card label="Total tax" value={totals.total_tax} color="#121C17" highlight />
        <Card label="Taxable value" value={totals.taxable_value} />
        <Card label="Input Tax Credit" value={totals.input_tax_credit} color="#25A36B" />
        <Card label="Tax payable" value={totals.tax_payable} color="#B45309" />
        <Card label="Tax paid" value={totals.tax_paid} color="#1E4D3B" />
      </div>

      {monthly.length > 0 && (
        <>
          <h3 className="label-eyebrow text-[10px] mb-2">Monthly tax trend</h3>
          <div className="h-64 -ml-2" data-testid="gst-chart-tax-trend">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                <XAxis dataKey="period_label" stroke="#5C6661" tick={{ fontSize: 11 }} />
                <YAxis stroke="#5C6661" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="cgst" stroke="#1E4D3B" name="CGST" strokeWidth={2} />
                <Line type="monotone" dataKey="sgst" stroke="#7BBF99" name="SGST" strokeWidth={2} />
                <Line type="monotone" dataKey="igst" stroke="#D17B5D" name="IGST" strokeWidth={2} />
                <Line type="monotone" dataKey="total_tax" stroke="#121C17" name="Total tax" strokeWidth={2.5} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {bills?.monthly?.length > 0 && (
        <>
          <h3 className="label-eyebrow text-[10px] mt-6 mb-2">Monthly invoice count</h3>
          <div className="h-56 -ml-2" data-testid="gst-chart-invoice-count">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={bills.monthly.slice().reverse()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E2DC" />
                <XAxis dataKey="period_label" stroke="#5C6661" tick={{ fontSize: 11 }} />
                <YAxis stroke="#5C6661" tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="invoice_count" fill="#D17B5D" name="Invoices" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
            {bills.monthly.slice(0, 6).map((m) => (
              <div key={m.period} className="rounded-xl bg-[#F3F1EC] p-3" data-testid={`gst-month-${m.period}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#121C17]">{m.period_label}</span>
                  <span className="text-xs text-[#5C6661]">{m.invoice_count} inv</span>
                </div>
                <div className="mt-1 text-xs text-[#5C6661]">Last bill: <span className="font-mono text-[#121C17]">{m.last_invoice_no}</span></div>
                <div className="mt-0.5 text-xs text-[#5C6661]">{(m.last_invoice_date || "").slice(0, 10)} · ₹{(m.monthly_total || 0).toLocaleString("en-IN")}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Card({ label, value, color = "#121C17", highlight }) {
  return (
    <div className={`p-4 rounded-xl ${highlight ? "border-2 border-[#1E4D3B] bg-[#1E4D3B]/5" : "border border-[#E5E2DC] bg-white"}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#5C6661]">{label}</div>
      <div className="mt-1.5 font-display font-medium text-xl" style={{ color }}>
        ₹{Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
      </div>
    </div>
  );
}
